import React from "react";
import { Progress } from "antd";

interface SkillsSection {
  score: number;
  max: number;
  matched?: number;
  required?: number;
}

interface ExperienceSection {
  score: number;
  max: number;
  candidate_years?: number;
  required_min?: number;
  required_max?: number;
}

interface SimpleSection {
  score: number;
  max: number;
}

interface ATSBreakdown {
  skills?: SkillsSection;
  experience?: ExperienceSection;
  education?: SimpleSection;
  keywords?: SimpleSection;
}

interface ATSScoreCardProps {
  score: number;
  breakdown: ATSBreakdown | null;
  onRecalculate?: () => void;
}

const SECTIONS: Array<{
  key: keyof ATSBreakdown;
  label: string;
  weight: string;
  color: string;
  icon: string;
}> = [
    { key: "skills", label: "Skills Matching", weight: "40%", color: "#4f46e5", icon: "ti-tools" },
    { key: "experience", label: "Experience", weight: "30%", color: "#0ea5e9", icon: "ti-briefcase" },
    { key: "education", label: "Education", weight: "15%", color: "#8b5cf6", icon: "ti-school" },
    { key: "keywords", label: "Keywords & Context", weight: "15%", color: "#f59e0b", icon: "ti-key" },
  ];

const getDetail = (key: keyof ATSBreakdown, section: any): string => {
  if (!section) return "";
  switch (key) {
    case "skills":
      return section.required > 0
        ? `${section.matched ?? 0} of ${section.required} required skills matched`
        : "No required skills defined for this role";
    case "experience":
      return section.candidate_years != null
        ? `Candidate has ${section.candidate_years}y (required: ${section.required_min}–${section.required_max}y)`
        : "Experience data not available";
    case "education":
      return section.score >= 15
        ? "Education meets or exceeds requirements"
        : section.score >= 8
          ? "Partially meets education requirements"
          : "Education may not meet requirements";
    case "keywords":
      return `${section.score} keyword points earned from resume analysis`;
    default:
      return "";
  }
};

const ATSScoreCard: React.FC<ATSScoreCardProps> = ({ score, breakdown, onRecalculate }) => {
  const roundedScore = Math.round(score);
  const isHigh = score > 80;
  const isMid = score >= 60 && score <= 80;

  const scoreColor = isHigh ? "#22c55e" : isMid ? "#f59e0b" : "#ef4444";
  const strokeGradient = isHigh
    ? { "0%": "#22c55e", "100%": "#16a34a" }
    : isMid
      ? { "0%": "#f59e0b", "100%": "#ea580c" }
      : { "0%": "#ef4444", "100%": "#dc2626" };

  return (
    <div
      className="rounded-4 overflow-hidden"
      style={{ background: "#fff", border: "1px solid #e8eaf0", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
    >
      
      <div style={{ height: 3, background: `linear-gradient(90deg,${scoreColor},${scoreColor}88)` }} />

      <div className="p-4">
        
        <div className="d-flex align-items-center gap-4 pb-4 mb-4" style={{ borderBottom: "1px solid #f1f5f9" }}>
          
          <div className="flex-shrink-0">
            <Progress
              type="circle"
              percent={roundedScore}
              strokeColor={strokeGradient}
              trailColor="#f1f3f5"
              size={96}
              strokeWidth={9}
              format={(pct) => (
                <span style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
                  {pct}<span style={{ fontSize: 13, fontWeight: 600 }}>%</span>
                </span>
              )}
            />
          </div>

          
          <div className="flex-grow-1">
            <h5 className="fw-bold mb-2 text-dark d-flex align-items-center gap-2" style={{ fontSize: 18 }}>
              Overall Match Score
              {isHigh && <i className="ti ti-discount-check-filled text-success" style={{ fontSize: 20 }} />}
              {isMid && <i className="ti ti-alert-circle-filled text-warning" style={{ fontSize: 20 }} />}
              {!isHigh && !isMid && <i className="ti ti-circle-x-filled text-danger" style={{ fontSize: 20 }} />}
            </h5>
            <div>
              {isHigh ? (
                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill" style={{ fontSize: 12 }}>
                  <i className="ti ti-thumb-up me-1" />Excellent match — highly recommended for interview
                </span>
              ) : isMid ? (
                <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-3 py-2 rounded-pill" style={{ fontSize: 12 }}>
                  <i className="ti ti-eye me-1" />Good match — worth reviewing further
                </span>
              ) : (
                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-3 py-2 rounded-pill" style={{ fontSize: 12 }}>
                  <i className="ti ti-thumb-down me-1" />Low match — may not meet requirements
                </span>
              )}
            </div>
          </div>

          {onRecalculate && (
            <button
              className="btn btn-sm d-flex align-items-center gap-2 rounded-pill px-4 flex-shrink-0"
              onClick={onRecalculate}
              style={{ background: "#f0f4ff", color: "#4f46e5", border: "1px solid #dde3ff", fontSize: 13, fontWeight: 600 }}
            >
              <i className="ti ti-refresh" style={{ fontSize: 16 }} />
              Recalculate
            </button>
          )}
        </div>

        
        {breakdown ? (
          <div className="row g-3">
            {SECTIONS.map(({ key, label, weight, color, icon }) => {
              const section = breakdown[key];
              if (!section) return null;
              const max = section.max ?? 100;
              const pct = max > 0 ? Math.round((section.score / max) * 100) : 0;
              const detail = getDetail(key, section);

              return (
                <div key={key} className="col-md-6">
                  <div
                    className="p-3 rounded-4 border h-100"
                    style={{ background: "#fafbfc", borderColor: "#e9ecef" }}
                  >
                    
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-2 d-flex align-items-center justify-content-center text-white flex-shrink-0"
                          style={{ width: 28, height: 28, background: color, fontSize: 14 }}
                        >
                          <i className={`ti ${icon}`} />
                        </div>
                        <span className="fw-bold text-dark" style={{ fontSize: 13 }}>{label}</span>
                        <span className="badge bg-light border text-muted rounded-pill" style={{ fontSize: 10 }}>
                          {weight}
                        </span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                        {section.score}
                        <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>/{max}</span>
                      </div>
                    </div>

                    
                    <Progress
                      percent={pct}
                      showInfo={false}
                      strokeColor={{ "0%": color, "100%": color + "BB" }}
                      trailColor="#f1f5f9"
                      strokeWidth={7}
                      className="mb-2"
                    />

                    
                    {detail && (
                      <div
                        className="rounded-3 px-2 py-1 d-flex align-items-center gap-1 mt-2"
                        style={{ background: color + "10", fontSize: 12, color: "#64748b" }}
                      >
                        <i className="ti ti-info-circle flex-shrink-0 mt-0" style={{ fontSize: 13, color }} />
                        {detail}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="ti ti-chart-pie d-block mb-3 opacity-25 text-muted" style={{ fontSize: 48 }} />
            <h6 className="fw-bold text-dark">No Breakdown Available</h6>
            <p className="text-muted mb-4" style={{ fontSize: 14 }}>
              Recalculate the score to generate a detailed breakdown.
            </p>
            {onRecalculate && (
              <button
                className="btn btn-primary rounded-pill px-4"
                onClick={onRecalculate}
              >
                <i className="ti ti-refresh me-2" style={{ fontSize: 16 }} />
                Recalculate Now
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ATSScoreCard;